import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { LogOut, Trash2, Plus, Star, Copy, Loader2, Upload, ExternalLink } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GALLERY_FILTERS } from "@/data/site";

const TABS = ["Quotes", "Reviews", "Gallery", "Areas", "Blog", "Settings"];
const input = "w-full border border-[var(--border)] px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--oxblood)]";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("Quotes");

  useEffect(() => { if (user === false) nav("/admin/login"); }, [user, nav]);
  if (!user || !user.email) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--oxblood)]" /></div>;

  return (
    <div className="min-h-screen bg-[var(--bone)]">
      <Toaster position="top-right" />
      <header className="bg-[var(--ink)] text-white sticky top-0 z-40">
        <div className="px-6 md:px-10 max-w-7xl mx-auto h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display tracking-[0.15em]">STONECREST</span>
            <span className="text-xs text-white/40 uppercase tracking-[0.2em]">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-xs text-white/60 hover:text-white flex items-center gap-1">View site <ExternalLink className="w-3 h-3" /></a>
            <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout" className="flex items-center gap-2 text-sm hover:text-[var(--oxblood)] transition-colors"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-10 max-w-7xl mx-auto py-8">
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[var(--border)]">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`} className={`px-5 py-3 text-sm tracking-wide -mb-px border-b-2 transition-colors ${tab === t ? "border-[var(--oxblood)] text-[var(--oxblood)]" : "border-transparent text-[var(--muted)] hover:text-[var(--ink)]"}`}>{t}</button>
          ))}
        </div>

        {tab === "Quotes" && <QuotesSection />}
        {tab === "Reviews" && <ReviewsSection />}
        {tab === "Gallery" && <GallerySection />}
        {tab === "Areas" && <AreasSection />}
        {tab === "Blog" && <BlogSection />}
        {tab === "Settings" && <SettingsSection />}
      </div>
    </div>
  );
}

function Card({ children, testid }) {
  return <div className="bg-white border border-[var(--border)] p-6" data-testid={testid}>{children}</div>;
}

/* ---------------- QUOTES ---------------- */
function QuotesSection() {
  const [quotes, setQuotes] = useState([]);
  const [link, setLink] = useState("");
  const [filter, setFilter] = useState("All");
  const load = () => api.get("/admin/quotes").then((r) => setQuotes(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/settings").then((r) => setLink(r.data.google_review_link || "")); }, []);

  const setStatus = async (id, status) => {
    const { data } = await api.patch(`/admin/quotes/${id}`, { status });
    if (status === "accepted") {
      if (data.review_email_sent) toast.success("Marked accepted — review request email sent to the customer.");
      else if (data.review_email_sent === false && link) toast.info("Marked accepted. (Review email already sent previously.)");
      else toast.info("Marked accepted. Add a Google review link in Settings so the review email includes it.");
    } else toast.success("Status updated");
    load();
  };
  const del = async (id) => { await api.delete(`/admin/quotes/${id}`); toast.success("Deleted"); load(); };

  const badge = { new: "bg-[var(--oxblood)] text-white", quoted: "bg-amber-500 text-white", accepted: "bg-emerald-600 text-white" };

  const isCommercial = (q) => q.property_category === "Commercial";
  const counts = {
    All: quotes.length,
    Residential: quotes.filter((q) => !isCommercial(q)).length,
    Commercial: quotes.filter(isCommercial).length,
  };
  const filtered = filter === "All" ? quotes : quotes.filter((q) => (filter === "Commercial" ? isCommercial(q) : !isCommercial(q)));

  if (quotes.length === 0) return <Card><p className="text-[var(--muted)]">No quote requests yet.</p></Card>;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="quotes-filter">
        {["All", "Residential", "Commercial"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`quotes-filter-${f.toLowerCase()}`}
            className={`px-4 py-2 text-sm rounded-full border transition-colors ${filter === f ? "bg-[var(--oxblood)] text-white border-[var(--oxblood)]" : "bg-white border-[var(--border)] text-[var(--ink)] hover:border-[var(--oxblood)]"}`}
          >
            {f} <span className={filter === f ? "text-white/70" : "text-[var(--muted)]"}>({counts[f]})</span>
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <Card><p className="text-[var(--muted)]">No {filter.toLowerCase()} enquiries.</p></Card>
      ) : filtered.map((q) => (
        <Card key={q.id} testid={`quote-row-${q.id}`}>
          <div className="flex flex-wrap justify-between gap-4">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h3 className="font-serif text-2xl">{q.name}</h3>
                {q.property_category && (
                  <span data-testid={`quote-category-${q.id}`} className={`text-[10px] uppercase tracking-wide px-2 py-1 ${q.property_category === "Commercial" ? "bg-[var(--ink)] text-white" : "bg-[var(--bone)] text-[var(--ink)]"}`}>{q.property_category}</span>
                )}
                <span className={`text-[10px] uppercase tracking-wide px-2 py-1 ${badge[q.status] || badge.new}`}>{q.status}</span>
              </div>
              <p className="text-sm text-[var(--muted)]">{q.phone} · {q.email}</p>
              <p className="text-sm text-[var(--muted)]">{q.address}</p>
              <p className="text-sm mt-2">{q.property_type && <b>{q.property_type}</b>}{q.bedrooms && ` · ${q.bedrooms} bed`}{(q.property_type || q.bedrooms) && " · "}Contact via {q.preferred_contact}</p>
              <p className="text-sm mt-1 text-[var(--oxblood)]">{(q.services || []).join(", ")}</p>
              {q.notes && <p className="text-sm mt-2 italic text-[var(--muted)]">"{q.notes}"</p>}
              {q.photo_urls?.length > 0 && (
                <div className="flex gap-2 mt-3">{q.photo_urls.map((u, i) => <a key={i} href={fileUrl(u)} target="_blank" rel="noreferrer"><img src={fileUrl(u)} alt="" className="w-16 h-16 object-cover border border-[var(--border)]" /></a>)}</div>
              )}
            </div>
            <div className="flex flex-col gap-2 items-end">
              <select value={q.status} onChange={(e) => setStatus(q.id, e.target.value)} data-testid={`quote-status-${q.id}`} className="border border-[var(--border)] px-3 py-2 text-sm">
                <option value="new">New</option><option value="quoted">Quoted</option><option value="accepted">Accepted</option>
              </select>
              <button onClick={() => del(q.id)} data-testid={`quote-delete-${q.id}`} className="text-sm text-[var(--muted)] hover:text-[var(--oxblood)] flex items-center gap-1"><Trash2 className="w-4 h-4" /> Delete</button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ---------------- REVIEWS ---------------- */
function ReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [f, setF] = useState({ author: "", rating: 5, text: "", location: "" });
  const load = () => api.get("/reviews").then((r) => setReviews(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!f.author || !f.text) return toast.error("Author and text required");
    await api.post("/admin/reviews", { ...f, rating: Number(f.rating) });
    setF({ author: "", rating: 5, text: "", location: "" }); toast.success("Review added"); load();
  };
  const del = async (id) => { await api.delete(`/admin/reviews/${id}`); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card testid="review-form">
        <h3 className="font-serif text-2xl mb-4">Add Review</h3>
        <div className="space-y-3">
          <input placeholder="Customer name" data-testid="review-author" value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} className={input} />
          <input placeholder="Location (e.g. Preston)" data-testid="review-location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={input} />
          <select value={f.rating} onChange={(e) => setF({ ...f, rating: e.target.value })} className={input}>{[5,4,3,2,1].map((n) => <option key={n} value={n}>{n} stars</option>)}</select>
          <textarea placeholder="Review text" data-testid="review-text" rows={4} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className={input} />
          <button onClick={add} data-testid="review-add-btn" className="bg-[var(--oxblood)] text-white px-6 py-3 text-sm flex items-center gap-2 hover:bg-[var(--oxblood-hover)]"><Plus className="w-4 h-4" /> Add Review</button>
        </div>
      </Card>
      <div className="space-y-3">
        {reviews.length === 0 && <Card><p className="text-[var(--muted)]">No reviews yet.</p></Card>}
        {reviews.map((r) => (
          <Card key={r.id} testid={`review-item-${r.id}`}>
            <div className="flex justify-between">
              <div className="flex text-[var(--oxblood)]">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-4 h-4" fill="currentColor" />)}</div>
              <button onClick={() => del(r.id)} className="text-[var(--muted)] hover:text-[var(--oxblood)]"><Trash2 className="w-4 h-4" /></button>
            </div>
            <p className="font-serif text-lg mt-2">"{r.text}"</p>
            <p className="text-sm text-[var(--muted)] mt-1">{r.author}{r.location ? ` · ${r.location}` : ""}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- GALLERY ---------------- */
function GallerySection() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ category: "Roof Cleaning", caption: "", image_url: "" });
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, image_url: data.url })); toast.success("Uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };
  const add = async () => {
    if (!f.image_url) return toast.error("Upload or paste an image URL");
    await api.post("/admin/gallery", { ...f, order: items.length });
    setF({ category: "Roof Cleaning", caption: "", image_url: "" }); toast.success("Added"); load();
  };
  const del = async (id) => { await api.delete(`/admin/gallery/${id}`); load(); };

  return (
    <div>
      <Card testid="gallery-form">
        <h3 className="font-serif text-2xl mb-4">Add Gallery Image</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={input}>{GALLERY_FILTERS.filter((x) => x !== "All").map((c) => <option key={c}>{c}</option>)}</select>
          <input placeholder="Caption" value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} className={input} />
          <label className="flex items-center justify-center gap-2 border border-dashed border-[var(--border)] py-3 cursor-pointer hover:border-[var(--oxblood)] text-sm">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload image
            <input type="file" accept="image/*" hidden data-testid="gallery-upload" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          </label>
          <input placeholder="…or paste image URL" value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} className={input} data-testid="gallery-url" />
        </div>
        {f.image_url && <img src={fileUrl(f.image_url)} alt="" className="w-32 h-24 object-cover mt-3 border border-[var(--border)]" />}
        <button onClick={add} data-testid="gallery-add-btn" className="mt-4 bg-[var(--oxblood)] text-white px-6 py-3 text-sm flex items-center gap-2 hover:bg-[var(--oxblood-hover)]"><Plus className="w-4 h-4" /> Add to Gallery</button>
      </Card>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {items.map((it) => (
          <div key={it.id} className="relative group border border-[var(--border)]" data-testid={`gallery-admin-${it.id}`}>
            <img src={fileUrl(it.image_url)} alt="" className="w-full aspect-square object-cover" />
            <span className="absolute top-2 left-2 bg-white/90 text-[10px] px-2 py-0.5">{it.category}</span>
            <button onClick={() => del(it.id)} className="absolute top-2 right-2 bg-[var(--ink)] text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- AREAS ---------------- */
function AreasSection() {
  const [areas, setAreas] = useState([]);
  const empty = { slug: "", name: "", title: "", meta_description: "", intro: "", content: "", active: true };
  const [f, setF] = useState(empty);
  const load = () => api.get("/areas").then((r) => setAreas(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.slug || !f.name) return toast.error("Slug and name required");
    await api.post("/admin/areas", f); setF(empty); toast.success("Saved"); load();
  };
  const del = async (id) => { await api.delete(`/admin/areas/${id}`); load(); };
  const edit = (a) => setF({ ...a });

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card testid="area-form">
        <h3 className="font-serif text-2xl mb-4">Add / Edit Area</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Slug (e.g. preston)" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase() })} className={input} data-testid="area-slug" />
            <input placeholder="Name (e.g. Preston)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={input} data-testid="area-name" />
          </div>
          <input placeholder="SEO Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={input} />
          <textarea placeholder="SEO Meta description" rows={2} value={f.meta_description} onChange={(e) => setF({ ...f, meta_description: e.target.value })} className={input} />
          <textarea placeholder="Intro (short)" rows={2} value={f.intro} onChange={(e) => setF({ ...f, intro: e.target.value })} className={input} />
          <textarea placeholder="Page content (use blank lines for paragraphs)" rows={5} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} className={input} />
          <button onClick={save} data-testid="area-save-btn" className="bg-[var(--oxblood)] text-white px-6 py-3 text-sm flex items-center gap-2 hover:bg-[var(--oxblood-hover)]"><Plus className="w-4 h-4" /> Save Area</button>
        </div>
      </Card>
      <div className="space-y-3">
        {areas.map((a) => (
          <Card key={a.id} testid={`area-item-${a.slug}`}>
            <div className="flex justify-between items-start">
              <div><h4 className="font-serif text-xl">{a.name}</h4><p className="text-xs text-[var(--muted)]">/areas/{a.slug}</p></div>
              <div className="flex gap-3">
                <button onClick={() => edit(a)} className="text-sm text-[var(--oxblood)]">Edit</button>
                <button onClick={() => del(a.id)} className="text-[var(--muted)] hover:text-[var(--oxblood)]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- BLOG ---------------- */
function BlogSection() {
  const [posts, setPosts] = useState([]);
  const empty = { slug: "", title: "", excerpt: "", content: "", cover_image: "", published: true };
  const [f, setF] = useState(empty);
  const load = () => api.get("/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.slug || !f.title) return toast.error("Slug and title required");
    await api.post("/admin/blog", f); setF(empty); toast.success("Saved"); load();
  };
  const del = async (id) => { await api.delete(`/admin/blog/${id}`); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card testid="blog-form">
        <h3 className="font-serif text-2xl mb-4">Publish Article</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Slug" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} className={input} data-testid="blog-slug" />
            <input placeholder="Cover image URL" value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} className={input} />
          </div>
          <input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={input} data-testid="blog-title" />
          <textarea placeholder="Excerpt" rows={2} value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} className={input} />
          <textarea placeholder="Content" rows={6} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} className={input} />
          <button onClick={save} data-testid="blog-save-btn" className="bg-[var(--oxblood)] text-white px-6 py-3 text-sm flex items-center gap-2 hover:bg-[var(--oxblood-hover)]"><Plus className="w-4 h-4" /> Publish</button>
        </div>
      </Card>
      <div className="space-y-3">
        {posts.length === 0 && <Card><p className="text-[var(--muted)]">No articles yet.</p></Card>}
        {posts.map((p) => (
          <Card key={p.id} testid={`blog-item-${p.slug}`}>
            <div className="flex justify-between items-start">
              <div><h4 className="font-serif text-xl">{p.title}</h4><p className="text-xs text-[var(--muted)]">/blog/{p.slug}</p></div>
              <button onClick={() => del(p.id)} className="text-[var(--muted)] hover:text-[var(--oxblood)]"><Trash2 className="w-4 h-4" /></button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- SETTINGS ---------------- */
function SettingsSection() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader2 className="w-5 h-5 animate-spin" />;

  const save = async () => {
    await api.put("/admin/settings", {
      ...s, average_rating: Number(s.average_rating) || 0, review_count: Number(s.review_count) || 0,
    });
    toast.success("Settings saved");
  };
  const field = (k, label, type = "text") => (
    <div><label className="block text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-2">{label}</label>
      <input type={type} value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className={input} data-testid={`setting-${k}`} /></div>
  );

  return (
    <Card testid="settings-form">
      <h3 className="font-serif text-2xl mb-2">Site Settings</h3>
      <p className="text-sm text-[var(--muted)] mb-6">The Google review link is emailed automatically to the customer when you mark a quote as <b>Accepted</b>.</p>
      <div className="grid sm:grid-cols-2 gap-5 max-w-3xl">
        {field("google_review_link", "Google Review Link")}
        {field("phone", "Phone")}
        {field("email", "Email")}
        {field("whatsapp", "WhatsApp Number (no +)")}
        <div className="sm:col-span-2">{field("address", "Address")}</div>
      </div>

      <div className="mt-8 pt-6 border-t border-[var(--border)]">
        <h4 className="font-serif text-xl mb-1">Reviews Display</h4>
        <p className="text-sm text-[var(--muted)] mb-4">Set an optional "Leave a review" link and the summary rating shown on the site. These sit alongside the reviews you manage above.</p>
        <div className="grid sm:grid-cols-2 gap-5 max-w-3xl">
          {field("google_review_link", "Leave a Review Link (optional)")}
          {field("average_rating", "Summary Rating", "number")}
          {field("review_count", "Review Count", "number")}
        </div>
      </div>

      <button onClick={save} data-testid="settings-save-btn" className="mt-6 bg-[var(--oxblood)] text-white px-8 py-3 text-sm hover:bg-[var(--oxblood-hover)]">Save Settings</button>
    </Card>
  );
}
